import React, { useState } from 'react';
import { rptApi } from '../services/api';
import '../styles/Modal.css';

function GenerateRPTModal({ entity, glVersion, onClose, onComplete }) {
  const [useAiDetection, setUseAiDetection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerateRPT = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Use start_date and end_date directly from glVersion
      const startDate = glVersion.start_date;
      const endDate = glVersion.end_date;

      if (!startDate || !endDate) {
        setError('GL version is missing start_date and end_date. Please re-fetch GL data.');
        setLoading(false);
        return;
      }

      const data = await rptApi.generateRPT(
        entity.name,
        startDate,
        endDate,
        useAiDetection
      );
      setResult(data);
      if (onComplete) {
        onComplete(data);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate RPT');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await rptApi.downloadRPT(entity.name, result.storage.timestamp, result.storage.period);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rpt_draft_${entity.name}_${result.storage.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download RPT file: ' + err.message);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const match = timestamp.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    }
    return timestamp;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate RPT from GL Data</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="info-box">
            <h4>Source GL Information:</h4>
            <p><strong>Period:</strong> {glVersion.period || 'N/A'}</p>
            <p><strong>Timestamp:</strong> {formatTimestamp(glVersion.timestamp)}</p>
            <p><strong>Total Entries:</strong> {glVersion.record_count?.toLocaleString() || 'N/A'}</p>
            <p><strong>Unique Transactions:</strong> {glVersion.unique_transactions?.toLocaleString() || 'N/A'}</p>
            <p><strong>Unique Accounts:</strong> {glVersion.unique_accounts?.toLocaleString() || 'N/A'}</p>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={useAiDetection}
                onChange={(e) => setUseAiDetection(e.target.checked)}
                disabled={loading}
              />
              <span>Use AI-based intercompany detection</span>
            </label>
            <small className="help-text">
              Enable Claude AI to detect intercompany transactions in addition to GL code matching
            </small>
          </div>

          {error && (
            <div className="alert alert-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {result && result.success && (
            <div className="alert alert-success">
              <h3>Successfully Generated RPT Draft</h3>
              <div className="result-details">
                <p><strong>Entity:</strong> {result.entity_name}</p>
                <p><strong>Total GL Entries:</strong> {result.summary.total_gl_entries?.toLocaleString()}</p>
                <p><strong>Filtered Entries:</strong> {result.summary.filtered_entries?.toLocaleString()}</p>
                <p><strong>Grouped Entries:</strong> {result.summary.grouped_entries?.toLocaleString()}</p>
                <p><strong>AI Detection:</strong> {result.summary.ai_detection_used ? 'Enabled' : 'Disabled'}</p>
                <p><strong>Status:</strong> <span className="badge-draft">Draft</span></p>
                <p><strong>Period:</strong> {result.storage.period}</p>
                <p><strong>Timestamp:</strong> {formatTimestamp(result.storage.timestamp)}</p>
              </div>
              <button
                className="btn btn-secondary"
                onClick={handleDownload}
                style={{ marginTop: '10px' }}
              >
                Download Draft RPT Excel
              </button>
              <p className="info-note" style={{ marginTop: '10px' }}>
                Download the Excel file, review the "Filtered GL Entries" sheet, and mark any
                transactions you want to exclude with "N" in the "Exclude from RPT" column.
                Then you can upload the modified file using the "Upload" button.
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleGenerateRPT}
              disabled={loading}
            >
              {loading ? 'Generating RPT...' : 'Generate RPT (Draft)'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenerateRPTModal;
